import React, { useEffect, useRef, useState } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { IoDocumentTextOutline, IoOpenOutline, IoRefresh, IoTrashOutline } from 'react-icons/io5';
import Styles from './styles.module.css';
import Dropzone from './Dropzone';
import Review from './Review';
import { analysisToDraft, buildConfidence } from './scanMapping';
import type { ScanDraft } from './scanMapping';
import {
  analyzeScan, subscribeScan, subscribeSchoolScans,
  saveScanDraft, completeScan, reopenScan, deleteScan,
  ScanDoc,
} from './scanApi';

type Props = {
  open: boolean;
  schoolId: string;
  schoolName: string;
  onClose: () => void;
};

const STATUS_LABEL: Record<string, string> = {
  uploading: 'アップロード中',
  processing: '読み取り中',
  review: '確認待ち',
  completed: '取込済み',
  error: 'エラー',
};

const ScanImport: React.FC<Props> = ({ open, schoolId, schoolName, onClose }) => {
  const [rows, setRows] = useState<ScanDoc[]>([]);
  const [activeScanId, setActiveScanId] = useState<string | null>(null);
  const [scan, setScan] = useState<ScanDoc | null>(null);

  const [uploading, setUploading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const draftComputing = useRef(false);

  useEffect(() => {
    if (!open || !schoolId) return;
    return subscribeSchoolScans(schoolId, setRows);
  }, [open, schoolId]);

  useEffect(() => {
    if (!activeScanId) { setScan(null); return; }
    return subscribeScan(activeScanId, setScan);
  }, [activeScanId]);

  // 確認画面に入ったが draft 未生成 → analysis から生成して保存
  useEffect(() => {
    if (!scan || scan.status !== 'review' || scan.draft || draftComputing.current) return;
    draftComputing.current = true;
    const draft = analysisToDraft(scan.analysis);
    const confidence = buildConfidence(scan.analysis);
    saveScanDraft(scan.id, draft, confidence)
      .catch(() => setErrorMsg('確認画面の準備に失敗しました。'))
      .finally(() => { draftComputing.current = false; });
  }, [scan?.id, scan?.status, scan?.draft]);

  if (!open) return null;

  const handleFile = async (file: File) => {
    setErrorMsg('');
    setUploading(true);
    try {
      const { scanId } = await analyzeScan({ schoolId, file });
      setActiveScanId(scanId);
    } catch (e: any) {
      setErrorMsg(e?.message || 'AI解析に失敗しました。もう一度お試しください。');
    } finally {
      setUploading(false);
    }
  };

  const handleRetry = async (scanId: string) => {
    setErrorMsg('');
    setRetrying(true);
    try {
      await analyzeScan({ schoolId, scanId });
      setActiveScanId(scanId);
    } catch (e: any) {
      setErrorMsg(e?.message || '再解析に失敗しました。');
    } finally {
      setRetrying(false);
    }
  };

  const handleSaveDraft = async (draft: ScanDraft) => {
    if (!scan) return;
    setSaving(true);
    try {
      await saveScanDraft(scan.id, draft, scan.confidence);
    } catch {
      setErrorMsg('下書きの保存に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (draft: ScanDraft) => {
    if (!scan) return;
    setSaving(true);
    try {
      await completeScan(scan.id, draft);
      setActiveScanId(null);
    } catch {
      setErrorMsg('登録に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (scanId: string) => {
    if (!window.confirm('このスキャンを削除しますか？')) return;
    try {
      await deleteScan(scanId);
      if (activeScanId === scanId) setActiveScanId(null);
    } catch {
      setErrorMsg('削除に失敗しました。');
    }
  };

  const body = () => {
    // フルスクリーンのローディング（新規アップロード〜解析完了まで）
    if (uploading) {
      return (
        <div className={Styles.center_state}>
          <AiOutlineLoading3Quarters className={Styles.spinner_lg} />
          <p className={Styles.center_title}>求人票を読み取っています…</p>
          <p className={Styles.center_sub}>PDFの内容をAIが整理しています。数十秒かかる場合があります。</p>
        </div>
      );
    }

    if (activeScanId && scan) {
      if (scan.status === 'processing') {
        return (
          <div className={Styles.center_state}>
            <AiOutlineLoading3Quarters className={Styles.spinner_lg} />
            <p className={Styles.center_title}>求人票を読み取っています…</p>
          </div>
        );
      }
      if (scan.status === 'error') {
        return (
          <div className={Styles.center_state}>
            <p className={Styles.center_title}>読み取りに失敗しました</p>
            <p className={Styles.center_sub}>{scan.error?.message || '不明なエラーが発生しました。'}</p>
            <div className={Styles.center_actions}>
              <button className={Styles.btn_secondary} onClick={() => setActiveScanId(null)}>一覧へ</button>
              <button className={Styles.btn_primary} disabled={retrying} onClick={() => handleRetry(scan.id)}>
                {retrying ? '再解析中…' : 'もう一度読み取る'}
              </button>
            </div>
          </div>
        );
      }
      if (scan.status === 'completed') {
        return (
          <div className={Styles.center_state}>
            <p className={Styles.center_title}>取込が完了しました</p>
            <p className={Styles.center_sub}>{scan.originalFileName}</p>
            <div className={Styles.center_actions}>
              <a className={Styles.btn_secondary} href={scan.pdfUrl} target="_blank" rel="noopener noreferrer">
                <IoOpenOutline /> 元のPDF
              </a>
              <button className={Styles.btn_secondary} onClick={() => reopenScan(scan.id)}>内容を再編集</button>
              <button className={Styles.btn_primary} onClick={() => setActiveScanId(null)}>一覧へ</button>
            </div>
          </div>
        );
      }
      if (scan.status === 'review' && scan.draft) {
        return (
          <Review
            scan={scan}
            saving={saving}
            onSaveDraft={handleSaveDraft}
            onSubmit={handleSubmit}
            onBack={() => setActiveScanId(null)}
          />
        );
      }
      return (
        <div className={Styles.center_state}>
          <AiOutlineLoading3Quarters className={Styles.spinner_lg} />
          <p className={Styles.center_title}>確認画面を準備しています…</p>
        </div>
      );
    }

    // 一覧 + ドロップゾーン
    return (
      <div className={Styles.list_wrap}>
        <Dropzone onFile={handleFile} onReject={setErrorMsg} disabled={uploading} />
        {errorMsg && <p className={Styles.error_line}>{errorMsg}</p>}

        <div className={Styles.list}>
          {rows.length === 0 && <p className={Styles.list_empty}>取り込んだ求人票はまだありません。</p>}
          {rows.map((r) => (
            <div key={r.id} className={Styles.list_row}>
              <IoDocumentTextOutline className={Styles.list_row_icon} />
              <button className={Styles.list_row_name} onClick={() => { setErrorMsg(''); setActiveScanId(r.id); }}>
                <span className={Styles.list_row_title}>{r.companyName || r.originalFileName}</span>
                <span className={Styles.list_row_sub}>{r.originalFileName}</span>
              </button>
              <span className={`${Styles.badge} ${Styles[`badge_${r.status}`] || ''}`}>
                {STATUS_LABEL[r.status] || r.status}
              </span>
              {r.status === 'error' && (
                <button className={Styles.row_icon_btn} title="もう一度読み取る" disabled={retrying} onClick={() => handleRetry(r.id)}>
                  <IoRefresh />
                </button>
              )}
              <a className={Styles.row_icon_btn} title="元のPDF" href={r.pdfUrl} target="_blank" rel="noopener noreferrer">
                <IoOpenOutline />
              </a>
              <button className={Styles.row_icon_btn} title="削除" onClick={() => handleDelete(r.id)}>
                <IoTrashOutline />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const headerTitle = activeScanId && scan?.status === 'review'
    ? '求人票の内容確認'
    : '求人票を取り込む';

  return (
    <div className={Styles.modal}>
      <div className={Styles.modal_bg} onClick={onClose} />
      <div className={Styles.modal_panel}>
        <div className={Styles.modal_header}>
          <h4>{headerTitle}{schoolName ? ` — ${schoolName}` : ''}</h4>
          <RxCross2 className={Styles.modal_close} onClick={onClose} />
        </div>
        <div className={Styles.modal_content}>{body()}</div>
      </div>
    </div>
  );
};

export default ScanImport;
